export const calculator = {
  hours: (row, monthDefaults) => {
    const fullCapacityFactor = (row.fullCapacity || 0) / 100;
    const result =
      (monthDefaults?.workingDays || 0) *
      (monthDefaults?.workingHoursPerDay || 0) *
      (row.currentSeats || 0) *
      fullCapacityFactor;
    return Number(result.toFixed(2));
  },

  sales: (row, monthDefaults) => {
    const totalHours = calculator.hours(row, monthDefaults);
    const result = totalHours * (row.salesPerHour || 0);
    // round to whole number
    return Math.round(result);
  },

  salesPerLeadRatio: (row) => {
    const salesToContact = (row.salesToContactRatio || 0) / 100;
    const contactToLeads = (row.contactToLeads || 0) / 100;

    if (!salesToContact || !contactToLeads) return 0;
    return salesToContact * contactToLeads; // Keep raw number for further math
  },

  totalBi: (row, monthDefaults) => {
    const totalSales = calculator.sales(row, monthDefaults);

    // --- ADD CONDITIONAL LOGIC FOR THE NEW CALCULATION ---
    if (row.calculationType === "Assupol BI Calculation") {
      const pFactorOnSale = row.pFactorOnSale || 0;
      const pFactorOnConversion = row.pFactorOnConversion || 0;
      const averagePremium = row.averagePremium || 0;
      const collectionRatio = (row.collectionRatio || 0) / 100; // Convert percentage to decimal
      const salesToLeadsRatio = calculator.salesPerLeadRatio(row);
      const leadFee = row.leadFee || 0;

      // Avoid division by zero
      const leadsPerSale = salesToLeadsRatio > 0 ? 1 / salesToLeadsRatio : 0;

      // Breaking down the formula for clarity
      const saleComponent = pFactorOnSale * averagePremium;
      const conversionComponent =
        pFactorOnConversion * averagePremium * collectionRatio;
      const leadCostComponent = leadsPerSale * leadFee;

      const biPerSingleSale =
        saleComponent + conversionComponent - leadCostComponent;

      const result = biPerSingleSale * totalSales;

      return Number(result.toFixed(2));
    }

    if (row.calculationType === "Assupol BI Calculation 2") {
      const averagePremium = row.averagePremium || 0;

      // Breaking down the formula for clarity

      const biPerSingleSale =
        4 * averagePremium * 0.5 + 3 * averagePremium - 500;

      const result = biPerSingleSale * totalSales;

      return Number(result.toFixed(2));
    }

    // for clientele (sales X rate per sale) + (sales X rate per converted sale X collection ratio)
    if (row.calculationType === "Clientele Legal BI Calculation") {
      const collectionRatio = (row.collectionRatio || 0) / 100; // Convert percentage to decimal
      const collectionRatioRate = row.collectionRatioRate || 0;

      const result =
        row.costPerSale * totalSales +
        collectionRatioRate * totalSales * collectionRatio;

      return Number(result.toFixed(2));
    }
    const biPerSale = row.biPerSale || 0;
    return Number((totalSales * biPerSale).toFixed(2));
  },

  biPerHour: (row) => {
    const biPerSale = row.biPerSale || 0;
    const result = biPerSale * (row.salesPerHour || 0);
    return Number(result.toFixed(2));
  },

  biPerSeat: (row, monthDefaults) => {
    const totalBi = calculator.totalBi(row, monthDefaults);
    const currentSeats = row.currentSeats || 0;
    if (!currentSeats) return 0;
    return Number((totalBi / currentSeats).toFixed(2));
  },

  totalPremium: (row, monthDefaults) => {
    const totalSales = calculator.sales(row, monthDefaults);
    const result = totalSales * (row.averagePremium || 0);
    return Number(result.toFixed(2));
  },

  leadsRequiredPerSeat: (row) => {
    const salesPerLead = calculator.salesPerLeadRatio(row);
    if (!salesPerLead) return 0;

    const leadsPerHour = (row.salesPerHour || 0) / salesPerLead;
    const result =
      leadsPerHour * (row.workingHoursPerDay || 0) * (row.workingDays || 0);

    return Number(Math.round(result));
  },

  totalLeadsRequired: (row, monthDefaults) => {
    const salesPerLead = calculator.salesPerLeadRatio(row);
    if (!salesPerLead || !row.salesPerHour) return 0;

    const leadsPerHour = row.salesPerHour / salesPerLead;
    const totalHours = calculator.hours(row, monthDefaults);
    return Number(Math.round(leadsPerHour * totalHours));
  },

  requiredLessConfirmedLeads: (row, monthDefaults) => {
    const totalLeadsRequired = calculator.totalLeadsRequired(
      row,
      monthDefaults,
    );
    const confirmedLeads = row.confirmedLeads || 0;
    return Number(Math.round(totalLeadsRequired - confirmedLeads));
  },

  AdditionalSeatsRequiredToMaxLeads: (row, monthDefaults) => {
    const totalLeadsRequired = calculator.totalLeadsRequired(
      row,
      monthDefaults,
    );
    const confirmedLeads = row.confirmedLeads || 0;
    const requiredLessConfirmed = calculator.requiredLessConfirmedLeads(
      row,
      monthDefaults,
    );
    const leadsPerSeat = calculator.leadsRequiredPerSeat(row);

    if (!leadsPerSeat) return 0;

    const result =
      totalLeadsRequired - confirmedLeads < 0
        ? -(requiredLessConfirmed / leadsPerSeat)
        : 0;

    return Number(result.toFixed(2));
  },

  capacityIncomeRisk: (row, monthDefaults) => {
    const biPerSeat = calculator.biPerSeat(row, monthDefaults);
    const additionalSeats = calculator.AdditionalSeatsRequiredToMaxLeads(
      row,
      monthDefaults,
    );

    if (!additionalSeats || !row.currentSeats || additionalSeats < 0) {
      return 0;
    }

    return Number((additionalSeats * biPerSeat).toFixed(2));
  },

  additionalLeadsRequiredToMaxSeats: (row, monthDefaults) => {
    const totalLeadsRequired = calculator.totalLeadsRequired(
      row,
      monthDefaults,
    );
    const confirmedLeads = row.confirmedLeads || 0;
    const diff = totalLeadsRequired - confirmedLeads;

    return Number((diff > 0 ? diff : 0).toFixed(2));
  },

  leadIncomeRisk: (row, monthDefaults) => {
    const additionalLeads = calculator.additionalLeadsRequiredToMaxSeats(
      row,
      monthDefaults,
    );
    const salesPerLeadRatio = calculator.salesPerLeadRatio(row);
    const biPerSale = row.biPerSale || 0;

    const result = additionalLeads * biPerSale * salesPerLeadRatio;
    return Number(result.toFixed(2));
  },
};
